import { FormEvent, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

interface TenantAddProps {
  onCreate: (input: { slug: string; displayName: string; replyTo?: string }) => Promise<void>;
}

const inputClass =
  'w-full rounded-lg border border-zinc-300 bg-white px-3.5 py-2.5 text-sm text-zinc-900 shadow-sm placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20';

export function TenantAdd({ onCreate }: TenantAddProps) {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onCreate({ slug, displayName, replyTo: replyTo || undefined });
      setSlug('');
      setDisplayName('');
      setReplyTo('');
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
            <path d="M8.75 3.75a.75.75 0 00-1.5 0v3.5h-3.5a.75.75 0 000 1.5h3.5v3.5a.75.75 0 001.5 0v-3.5h3.5a.75.75 0 000-1.5h-3.5v-3.5z" />
          </svg>
          Add Tenant
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-zinc-200">
          <Dialog.Title className="text-base font-semibold text-zinc-900">New Tenant</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-zinc-500">
            Provisions SES identity and DNS records for a new sending domain.
          </Dialog.Description>
          <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700" htmlFor="tenant-slug">Slug</label>
              <input
                id="tenant-slug"
                className={inputClass}
                value={slug}
                placeholder="e.g. acme"
                onChange={(e) => setSlug(e.target.value)}
                required
              />
              <p className="text-xs text-zinc-400">Lowercase letters, numbers, hyphens. Used in the email domain.</p>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700" htmlFor="tenant-display-name">Display Name</label>
              <input
                id="tenant-display-name"
                className={inputClass}
                value={displayName}
                placeholder="e.g. Acme Co"
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700" htmlFor="tenant-reply-to">
                Reply-To <span className="font-normal text-zinc-400">(optional)</span>
              </label>
              <input
                id="tenant-reply-to"
                className={inputClass}
                value={replyTo}
                placeholder="e.g. support@acme.com"
                onChange={(e) => setReplyTo(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-zinc-100 pt-4">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-lg px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:opacity-60"
              >
                {submitting ? 'Creating…' : 'Create Tenant'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
